/**
 * @fileoverview System-web authentication provider and authenticated user context.
 * @module system-web/auth
 *
 * Responsibilities:
 * - Bootstrap MSAL configuration and expose authenticated user state to the app.
 * - Gate protected routes until the system portal user is fully bootstrapped.
 *
 * Dependencies:
 * - `@azure/msal-browser`
 * - `@azure/msal-react`
 * - `../config`
 *
 * Architecture:
 * - Auth state is the prerequisite for opening the local-first system Dexie
 *   database and starting replication.
 * - All environments must flow through MSAL so local development exercises the
 *   same identity bootstrap and authorization path as deployed environments.
 *
 * Notes:
 * - Synthetic auth shortcuts are not permitted because they mask real identity
 *   and permission defects in the system portal bootstrap flow.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { getWebConfig } from '../config';
import { fetchAuthConfig, buildMsalConfig, getLoginScopes, AuthConfigResponse } from './msalConfig';
// Shared, framework-agnostic MSAL token acquisition (single source of truth
// across tenant-web / system-web / mobile-pwa): acquireTokenSilent + 5-minute
// proactive expiry buffer + forceRefresh.
import { acquireFreshToken } from '@firesquirrel/web-auth';
import { setTokenProvider } from '../apiClient';
import { setSystemAuditUser, clearSystemAuditUser } from '../services/systemAuditLogService';
import { parseUserAgent } from '../utils/parseUserAgent';
import ConfirmActionModal from '../components/ui/ConfirmActionModal';
import SystemAccessDeniedScreen from '../components/auth/SystemAccessDeniedScreen';
import SystemAccountNotFoundScreen from '../components/auth/SystemAccountNotFoundScreen';
import SystemSessionExpiredScreen from '../components/auth/SystemSessionExpiredScreen';
import {
  ensureStoragePersistence,
  STORAGE_CRITICAL_THRESHOLD,
  useStorageHealthPolling,
  getStoragePersistenceMessage,
  getStorageCriticalMessage,
} from '@firesquirrel/local-db';

const BROWSER_SSO_SESSION_PREFIX = 'firesquirrel_system_browser_sso_attempted';
const SESSION_EXPIRED_MESSAGE = 'Your sign-in session could not be verified. Please sign in again to continue.';

/**
 * @contract StorageModalState
 *
 * Purpose:
 * - Describes a storage warning modal shown during auth and app-shell startup.
 *
 * Fields:
 * - title: user-facing modal heading.
 * - description: user-facing explanation of the storage condition.
 * - warning: user-facing risk or action guidance.
 *
 * Rules:
 * - Values must be display-safe strings produced by storage health services.
 *
 * Sync:
 * - Local-only UI state; no server synchronization.
 */
interface StorageModalState {
  title: string;
  description: string;
  warning: string;
}

/**
 * @contract AuthContextValue
 *
 * Purpose:
 * - Represents the system-web authentication state exposed to routed UI.
 *
 * Fields:
 * - isAuthenticated: true only after MSAL and server login-sync both succeed.
 * - isLoading: true while MSAL redirect, SSO, or login-sync bootstrap is active.
 * - syncReady: true when local-first system Dexie sync may start.
 * - user: canonical system user derived from the login-sync trust boundary.
 * - fatalAuthError: authorization or account-state error that blocks the portal.
 * - login: interactive Microsoft sign-in action.
 * - logout: Microsoft sign-out action and local auth cleanup.
 * - authConfigured: true when the API has returned usable MSAL configuration.
 * - acquireGraphToken: helper for Microsoft Graph-scoped token requests.
 *
 * Rules:
 * - Components must treat syncReady as the gate for local-first replication.
 * - Server login-sync is authoritative for system portal access.
 *
 * Sync:
 * - Auth state gates local-first sync; auth records themselves are not written to Dexie here.
 */
interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  syncReady: boolean;
  user: { id: string; entraUserId?: string; name: string; email: string; permissions: string[] } | null;
  fatalAuthError: { code: string; message: string } | null;
  login: () => void;
  logout: () => void;
  authConfigured: boolean;
  acquireGraphToken: (scopes: string[]) => Promise<string | null>;
}

/**
 * @process BrowserSsoAttempt
 *
 * Steps:
 * 1. Prefer cached MSAL accounts because they already identify the browser user for this client.
 * 2. Attempt iframe-based ssoSilent for browsers that allow Entra cookies in a hidden frame.
 * 3. Fall back to one top-level prompt=none redirect so an existing Entra browser session can SSO without UI.
 *
 * Inputs:
 * - cfg: runtime MSAL configuration from the system API.
 * - pca: initialized PublicClientApplication instance.
 *
 * Outputs:
 * - Active MSAL account, redirect navigation, or unauthenticated login page after the one-shot attempt fails.
 *
 * Failure Handling:
 * - Silent SSO failures are logged at info level and do not become access-denied authorization failures.
 *
 * Idempotency:
 * - REQUIRED; sessionStorage records one prompt=none redirect attempt per client/origin browser tab.
 */
async function attemptExistingBrowserSso(
  pca: PublicClientApplication,
  cfg: AuthConfigResponse,
): Promise<'account' | 'redirect' | 'none'> {
  const accounts = pca.getAllAccounts();
  if (accounts.length > 0) {
    pca.setActiveAccount(accounts[0]);
    void ensureStoragePersistence('auth-restored-session');
    return 'account';
  }

  try {
    const silentResponse = await pca.ssoSilent({
      scopes: getLoginScopes(cfg),
    });
    if (silentResponse?.account) {
      pca.setActiveAccount(silentResponse.account);
      void ensureStoragePersistence('auth-restored-browser-session');
      return 'account';
    }
  } catch (silentErr) {
    if (isInteractionRequiredError(silentErr)) {
      console.info('[auth] No existing iframe browser session; continuing to login.');
    } else {
      console.info('[auth] iframe browser SSO unavailable; continuing to login.');
    }
  }

  if (!isBrowserSsoRedirectEligible(window.location.pathname)) {
    return 'none';
  }

  const redirectAttemptKey = getBrowserSsoRedirectAttemptKey(cfg);
  if (hasBrowserSsoRedirectBeenAttempted(redirectAttemptKey)) {
    return 'none';
  }

  markBrowserSsoRedirectAttempted(redirectAttemptKey);
  await pca.loginRedirect({
    scopes: getLoginScopes(cfg),
    prompt: 'none',
    redirectStartPage: window.location.href,
  });
  return 'redirect';
}

/**
 * @security
 *
 * Validation:
 * - Only known public/auth routes are excluded from automatic browser SSO.
 *
 * Trust Boundary:
 * - The redirect still returns through MSAL and login-sync; no local user is trusted from browser state alone.
 */
function isBrowserSsoRedirectEligible(pathname: string): boolean {
  return !pathname.startsWith('/auth/') && pathname !== '/brand-preview';
}

function getBrowserSsoRedirectAttemptKey(cfg: AuthConfigResponse): string {
  return `${BROWSER_SSO_SESSION_PREFIX}:${cfg.clientId || 'unknown'}:${window.location.origin}`;
}

function hasBrowserSsoRedirectBeenAttempted(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markBrowserSsoRedirectAttempted(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // Storage may be unavailable in locked-down browser modes; continue with normal login UI.
  }
}

/**
 * @errorHandling
 *
 * Strategy:
 * - Classify MSAL and Entra errors that require top-level user interaction as recoverable session expiry.
 *
 * User Impact:
 * - The app shows a Sign In Again prompt instead of an access-denied screen.
 *
 * Logging:
 * - REQUIRED; callers log the original error before switching UI state.
 */
function isInteractionRequiredError(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  const candidate = err as { errorCode?: string; message?: string };
  const errorText = `${candidate?.errorCode ?? ''} ${candidate?.message ?? ''}`.toLowerCase();
  return errorText.includes('interaction_required')
    || errorText.includes('login_required')
    || errorText.includes('consent_required')
    || errorText.includes('no_tokens_found')
    || errorText.includes('no_account')
    || errorText.includes('monitor_window_timeout')
    || errorText.includes('user_login_error')
    || errorText.includes('invalid_grant')
    || errorText.includes('token_renewal_error')
    || errorText.includes('browser_auth_error')
    || errorText.includes('aadsts160021')
    || errorText.includes('aadsts50058')
    || errorText.includes('aadsts65001')
    || errorText.includes('aadsts700082');
}

// ---------------------------------------------------------------------------
// loginSync — called after MSAL login to sync identity to MongoDB
// Returns structured result so the caller can block on 403/404 (fatal errors).
// Network errors are non-fatal (user may be offline) — return ok:true to not block.
// ---------------------------------------------------------------------------
async function loginSync(
  accessToken: string,
  claims: Record<string, any>,
): Promise<{ ok: boolean; status: number; code?: string; userId?: string; loginEventId?: string | null }> {
  try {
    const res = await fetch('/api/auth/login-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({
        entraUserId: claims.oid || claims.sub,
        email: claims.email || claims.preferred_username,
        name: claims.name,
        signInType: claims.idp ? 'federated' : 'emailAddress',
        issuer: claims.iss,
        issuerAssignedId: claims.email || claims.preferred_username,
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, status: res.status, code: body.code };
    }
    const body = await res.json().catch(() => ({}));
    return { ok: true, status: res.status, userId: body.userId, loginEventId: body.loginEventId ?? null };
  } catch (err) {
    console.warn('[auth] login-sync network error (non-fatal):', err);
    return { ok: true, status: 0 };
  }
}

// Phase 2: enrich the login audit entry with client-side device/browser/OS data.
// Fire-and-forget — errors are silent and never block the login flow.
async function enrichLoginEvent(loginEventId: string, accessToken: string): Promise<void> {
  try {
    const parsed = parseUserAgent(navigator.userAgent);
    await fetch(`/api/auth/login-events/${loginEventId}/enrich`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({
        browser: parsed.browser,
        browserVersion: parsed.browserVersion,
        os: parsed.os,
        osVersion: parsed.osVersion,
        deviceType: parsed.deviceType,
        screenWidth: typeof screen !== 'undefined' ? screen.width : undefined,
        screenHeight: typeof screen !== 'undefined' ? screen.height : undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
      }),
    });
  } catch {
    // non-fatal
  }
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  syncReady: false,
  user: null,
  fatalAuthError: null,
  login: () => {},
  logout: () => {},
  authConfigured: false,
  acquireGraphToken: async () => null,
});

/**
 * @hook useAuth
 *
 * Purpose:
 * - Provide the current authentication/bootstrap state for system-web.
 *
 * Returns:
 * - `isAuthenticated`, `isLoading`, `user`, and auth actions/token helpers.
 *
 * Side Effects:
 * - None directly; reads from AuthContext only.
 *
 * Offline Behavior:
 * - Auth bootstrap is still required before the local-first database opens.
 */
export function useAuth() {
  return useContext(AuthContext);
}

function MsalAuthInner({ children, authConfig }: { children: React.ReactNode; authConfig: AuthConfigResponse }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] ?? null;
  const [fatalAuthError, setFatalAuthError] = useState<{ code: string; message: string } | null>(null);
  const [sessionExpiredError, setSessionExpiredError] = useState<string | null>(null);
  const [syncReady, setSyncReady] = useState(false);
  const [user, setUser] = useState<AuthContextValue['user']>(null);
  const [authBootstrapState, setAuthBootstrapState] = useState<'idle' | 'bootstrapping' | 'ready' | 'fatal'>('idle');
  const bootstrappedAccountRef = React.useRef<string>('');
  const accountIdentity = account
    ? `${account.homeAccountId}|${account.localAccountId}|${account.username}`
    : '';

  // Set synchronously during render — NOT in useEffect — so getToken() works
  // immediately when isAuthenticated becomes true and startSync fires.
  // useEffect would run after render, creating a window where _getToken is null
  // and the first sync call returns no auth header → 401 → 5-minute backoff.
  setTokenProvider(async (options) => {
    if (!account) return null;
    const scopes = getLoginScopes(authConfig);
    try {
      // Shared pull-model acquisition: acquireTokenSilent + 5-min proactive
      // buffer + forceRefresh. Forwards a caller-requested forceRefresh so the
      // server never rejects a stale token into a 401 retry/backoff loop.
      return await acquireFreshToken(instance, account, scopes, {
        forceRefresh: options?.forceRefresh === true,
      });
    } catch (err) {
      if (isInteractionRequiredError(err)) {
        console.warn('[auth] token provider requires interactive authentication:', err);
        setSessionExpiredError(SESSION_EXPIRED_MESSAGE);
        setSyncReady(false);
        return null;
      }
      return null;
    }
  });

  const login = useCallback(async () => {
    const scopes = getLoginScopes(authConfig);
    console.log('[auth] login called, scopes:', scopes);
    try {
      await instance.loginRedirect({ scopes });
    } catch (err: any) {
      console.error('[auth] loginRedirect error:', err?.errorCode, err?.message || err);
      // If redirect fails due to interaction in progress, try popup
      if (err?.errorCode === 'interaction_in_progress') {
        try {
          const result = await instance.loginPopup({ scopes });
          if (result?.account) {
            instance.setActiveAccount(result.account);
          }
        } catch (popupErr) {
          console.error('[auth] loginPopup error:', popupErr);
        }
      }
    }
  }, [instance, authConfig]);

  const logout = useCallback(() => {
    clearSystemAuditUser();
    setFatalAuthError(null);
    setSessionExpiredError(null);
    setSyncReady(false);
    setAuthBootstrapState('idle');
    setUser(null);
    instance.logoutRedirect();
  }, [instance]);

  const acquireGraphToken = useCallback(async (scopes: string[]): Promise<string | null> => {
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch (err) {
      if (isInteractionRequiredError(err)) {
        console.warn('[auth] Graph token acquisition requires interactive authentication:', err);
        setSessionExpiredError(SESSION_EXPIRED_MESSAGE);
        setSyncReady(false);
        return null;
      }
      console.error('[auth] acquireGraphToken error:', err);
      return null;
    }
  }, [instance, account]);

  useEffect(() => {
    bootstrappedAccountRef.current = '';
    clearSystemAuditUser();
    setFatalAuthError(null);
    setSessionExpiredError(null);
    setSyncReady(false);

    if (!account) {
      setUser(null);
      setAuthBootstrapState('idle');
      return;
    }

    setUser(null);
    setAuthBootstrapState('idle');
  }, [accountIdentity]);

  useEffect(() => {
    if (!account) return;
    if (bootstrappedAccountRef.current === accountIdentity) {
      return;
    }

    let cancelled = false;
    bootstrappedAccountRef.current = accountIdentity;
    setAuthBootstrapState('bootstrapping');

    (async () => {
      try {
        let tokenResult = await instance.acquireTokenSilent({
          scopes: getLoginScopes(authConfig),
          account,
        });
        let accessToken = tokenResult.idToken || tokenResult.accessToken;
        const claims = tokenResult.idTokenClaims as Record<string, any>;

        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            const expiresAt = payload.exp * 1000;
            const REFRESH_BUFFER_MS = 5 * 60 * 1000;
            if (Date.now() > expiresAt - REFRESH_BUFFER_MS) {
              tokenResult = await instance.acquireTokenSilent({
                scopes: getLoginScopes(authConfig),
                account,
                forceRefresh: true,
              });
              accessToken = tokenResult.idToken || tokenResult.accessToken;
            }
          } catch { /* decode failed — use token as-is */ }
        }

        if (!accessToken) {
          throw new Error('MSAL did not return an access token for login-sync');
        }

        const result = await loginSync(accessToken, claims);
        if (!result.ok) {
          if (cancelled) return;
          if (result.status === 403) {
            const code = result.code || 'NOT_SYSTEM_ADMIN';
            const message = code === 'ACCOUNT_INACTIVE'
              ? 'Your account has been deactivated. Contact your administrator.'
              : 'You do not have access to the system portal.';
            setFatalAuthError({ code, message });
            setAuthBootstrapState('fatal');
            return;
          }
          if (result.status === 404) {
            if (cancelled) return;
            clearSystemAuditUser();
            setUser(null);
            setFatalAuthError({ code: 'USER_NOT_FOUND', message: 'Your account was not found in the system. Contact your administrator.' });
            setAuthBootstrapState('fatal');
            return;
          }
        } else if (!result.userId && result.status !== 0) {
          if (cancelled) return;
          clearSystemAuditUser();
          setUser(null);
          setFatalAuthError({ code: 'BOOTSTRAP_FAILED', message: 'We were unable to complete sign-in. Please try again.' });
          setAuthBootstrapState('fatal');
          return;
        } else if (result.loginEventId) {
          void enrichLoginEvent(result.loginEventId, accessToken);
        }

        const resolvedUser = {
          id: result.userId || '',
          entraUserId: claims.oid || claims.sub || account.homeAccountId,
          name: account.name || '',
          email: account.username || '',
          permissions: ['*'],
        };

        if (cancelled) return;

        setFatalAuthError(null);
        setSyncReady(true);
        setUser(resolvedUser);
        setAuthBootstrapState('ready');

        if (result.userId) {
          setSystemAuditUser({
            userId: result.userId,
            email: resolvedUser.email,
            name: resolvedUser.name,
          });
        } else {
          clearSystemAuditUser();
        }

      } catch (err) {
        console.warn('[auth] login-sync token acquisition failed; interactive authentication required:', err);
        if (cancelled) return;
        if (isInteractionRequiredError(err)) {
          clearSystemAuditUser();
          setSessionExpiredError(SESSION_EXPIRED_MESSAGE);
          setFatalAuthError(null);
          setSyncReady(false);
          setUser(null);
          setAuthBootstrapState('idle');
          return;
        }
        clearSystemAuditUser();
        setSyncReady(false);
        setUser(null);
        setFatalAuthError({
          code: 'BOOTSTRAP_FAILED',
          message: 'We were unable to complete sign-in. Please try again.',
        });
        setAuthBootstrapState('fatal');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountIdentity, instance, authConfig]);

  // MSAL is still processing a redirect/popup/ssoSilent — treat as loading
  // so the boot splash stays visible and LoginPage doesn't flash.
  const msalBusy = inProgress !== InteractionStatus.None;

  const isBootstrappingAuth = msalBusy || (isAuthenticated
    && !!account
    && !fatalAuthError
    && !user
    && authBootstrapState !== 'ready'
    && authBootstrapState !== 'fatal');

  useEffect(() => {
    if (!account) {
      clearSystemAuditUser();
      setUser(null);
    }
  }, [account]);

  const value: AuthContextValue = {
    isAuthenticated: isAuthenticated && authBootstrapState === 'ready',
    isLoading: isBootstrappingAuth,
    syncReady,
    user,
    fatalAuthError,
    login,
    logout,
    authConfigured: true,
    acquireGraphToken,
  };

  const reauthenticate = () => {
    setSessionExpiredError(null);
    setFatalAuthError(null);
    setAuthBootstrapState('idle');
    login();
  };

  if (sessionExpiredError) {
    document.getElementById('boot-screen')?.classList.add('hidden');
    return <SystemSessionExpiredScreen message={sessionExpiredError} onLogin={reauthenticate} />;
  }

  if (fatalAuthError) {
    // Hide boot splash so error screens are visible
    document.getElementById('boot-screen')?.classList.add('hidden');
    if (fatalAuthError.code === 'USER_NOT_FOUND') {
      return <SystemAccountNotFoundScreen onLogout={logout} />;
    }
    return <SystemAccessDeniedScreen onLogout={logout} />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * @component DevBypassAuthProvider
 *
 * Purpose:
 * - Dev/stage auth context provider that short-circuits MSAL when
 *   VITE_AUTH_BYPASS=true and appEnv is not 'prod'. Lets E2E tests, local
 *   development, and stage run without an Entra backend.
 *
 * Props:
 * - children: application subtree.
 *
 * Behavior:
 * - Sets isAuthenticated=true, syncReady=true, and a synthetic dev admin user
 *   immediately on mount. No MSAL initialization or network calls.
 *
 * Data Source:
 * - local (synthetic dev session; no server trust).
 *
 * Constraints:
 * - MUST only be active when appEnv !== 'prod' AND VITE_AUTH_BYPASS is truthy.
 *   The flag is silently ignored in production — see CLAUDE.md setup guide.
 *
 * Performance:
 * - No async bootstrap; renders children synchronously.
 */
function DevBypassAuthProvider({ children }: { children: React.ReactNode }) {
  const [syncReady, setSyncReady] = useState(false);

  useEffect(() => {
    // Set the audit user so system audit logs have a user context in dev bypass.
    setSystemAuditUser({
      userId: 'dev-system-admin',
      email: 'dev@firesquirrel.local',
      name: 'Dev System Admin',
    });
    // Provide a dummy token so apiClient includes an Authorization header.
    // The backend APIs run with AUTH_BYPASS=true in dev/stage, so the token
    // value is not validated — it just needs to be present.
    setTokenProvider(async () => 'dev-bypass-token');
    // Simulate the sync-ready gate so local-first replication can start.
    const t = setTimeout(() => setSyncReady(true), 0);
    return () => {
      clearTimeout(t);
      clearSystemAuditUser();
    };
  }, []);

  const value: AuthContextValue = {
    isAuthenticated: true,
    isLoading: false,
    syncReady,
    user: {
      id: 'dev-system-admin',
      name: 'Dev System Admin',
      email: 'dev@firesquirrel.local',
      permissions: ['*'],
    },
    fatalAuthError: null,
    login: () => {},
    logout: () => {},
    authConfigured: true,
    acquireGraphToken: async () => null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function MsalAuthProvider({ children }: { children: React.ReactNode }) {
  const [authConfig, setAuthConfig] = useState<AuthConfigResponse | null>(null);
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [storageModal, setStorageModal] = useState<StorageModalState | null>(null);
  const [authRetryCount, setAuthRetryCount] = useState(0);
  const initRef = React.useRef(false);
  const storageModalGateRef = React.useRef<Record<string, boolean>>({});
  const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const { health: storageHealth } = useStorageHealthPolling(true);

  useEffect(() => {
    const showOnce = (key: string, modal: StorageModalState) => {
      if (storageModalGateRef.current[key]) return;
      try { if (sessionStorage.getItem('fs_storage_warn_dismissed')) return; } catch {}
      storageModalGateRef.current[key] = true;
      setStorageModal(modal);
    };

    // One-time persist request on startup
    const initPersistence = async () => {
      const persistence = await ensureStoragePersistence('app-shell-startup');
      const isDev = getWebConfig().appEnv === 'dev';
      if (!isDev && !persistence.granted && persistence.outcome !== 'already-persisted') {
        const outcome = persistence.outcome as 'denied' | 'unsupported' | 'error';
        showOnce(`persist-${persistence.outcome}`, getStoragePersistenceMessage(outcome));
      }
    };

    void initPersistence();
  }, []);

   useEffect(() => {
     if (storageHealth.percentUsed >= STORAGE_CRITICAL_THRESHOLD) {
       if (storageModalGateRef.current['critical-storage']) return;
       try { if (sessionStorage.getItem('fs_storage_warn_dismissed')) return; } catch {}
       storageModalGateRef.current['critical-storage'] = true;
       setStorageModal(getStorageCriticalMessage(storageHealth.percentUsed));
     }
   }, [storageHealth.percentUsed]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    setLoading(true);

    // Capture hash immediately before anything can clear it
    const urlHash = window.location.hash;
    (async () => {
      try {
        const CACHE_KEY = 'firesquirrel_auth_config';
        const CACHE_TTL_MS = 15 * 60 * 1000;

        const readCache = (): AuthConfigResponse | null => {
          try {
            const raw = localStorage.getItem(CACHE_KEY);
            if (!raw) return null;
            const { cfg, cachedAt } = JSON.parse(raw);
            if (!cfg?.configured) return null;
            if (Date.now() - cachedAt > CACHE_TTL_MS) return null;
            return cfg as AuthConfigResponse;
          } catch { return null; }
        };

        const writeCache = (cfg: AuthConfigResponse) => {
          if (!cfg.configured) return;
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({ cfg, cachedAt: Date.now() })); }
          catch { /* storage full */ }
        };

        let cfg: AuthConfigResponse;

        // On callback page with a valid cached config, skip the network round-trip
        const cached = readCache();
        if (cached) {
          cfg = cached;
        } else {
          cfg = await fetchAuthConfig();
          writeCache(cfg);
        }

        if (!cfg.configured) {
          const RETRY_DELAYS = [3_000, 5_000, 10_000, 15_000, 30_000];
          const delay = RETRY_DELAYS[Math.min(authRetryCount, RETRY_DELAYS.length - 1)];
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
          retryTimeoutRef.current = setTimeout(() => {
            retryTimeoutRef.current = null;
            initRef.current = false;
            setAuthRetryCount(c => c + 1);
            setLoading(true);
          }, delay);
          setLoading(false);
          return;
        }

        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }

        setAuthConfig(cfg);

        if (cfg.configured && cfg.clientId) {
          const pca = new PublicClientApplication(buildMsalConfig(cfg));
          await pca.initialize();
          try {
            const response = await pca.handleRedirectPromise(urlHash || undefined);
            if (response?.account) {
              pca.setActiveAccount(response.account);
              void ensureStoragePersistence('auth-success');
              if (window.location.pathname === '/auth/callback') {
                window.location.replace('/');
                return;
              }
            } else {
              const ssoResult = await attemptExistingBrowserSso(pca, cfg);
              if (ssoResult === 'redirect') {
                return;
              }
            }
          } catch (err) {
            if (isInteractionRequiredError(err)) {
              // A signed-out browser naturally returns login_required from the
              // prompt=none SSO probe. This is an expected route to LoginPage,
              // not an application error and should not produce a scary console
              // stack trace.
              console.info('[auth] No existing browser session; showing login screen.');
            } else {
              console.warn('[auth] redirect error:', err);
            }
          }

          setMsalInstance(pca);
        }
      } catch (err: any) {
        console.error('[auth] init error:', err);
        setInitError(err?.message || 'Failed to initialize authentication');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [authRetryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return null; // HTML boot splash stays visible
  }

  if (initError) {
    // Hide boot splash so error UI is visible
    document.getElementById('boot-screen')?.classList.add('hidden');
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">&#x26A0;</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Connection Error</h1>
          <p className="text-gray-600 text-sm">
            Unable to connect to the FireSquirrel API. Please check that the backend service is running and try again.
          </p>
          <p className="text-gray-400 text-xs mt-4 font-mono">{initError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }


  if (authConfig?.configured && msalInstance) {
    return (
      <>
        <MsalProvider instance={msalInstance}>
          <MsalAuthInner authConfig={authConfig}>{children}</MsalAuthInner>
        </MsalProvider>
        {storageModal && (
          <ConfirmActionModal
            title={storageModal.title}
            description={storageModal.description}
            warning={storageModal.warning}
            confirmButtonLabel="I Understand"
            variant="warning"
            onConfirm={() => {
              try { sessionStorage.setItem('fs_storage_warn_dismissed', '1'); } catch {}
              setStorageModal(null);
            }}
            onClose={() => {
              try { sessionStorage.setItem('fs_storage_warn_dismissed', '1'); } catch {}
              setStorageModal(null);
            }}
          />
        )}
      </>
    );
  }

  return null; // HTML boot splash stays visible
}

/**
 * @component AuthProvider
 *
 * Purpose:
 * - Wrap system-web with the authenticated user context required for protected
 *   routes and local-first database startup.
 *
 * Props:
 * - children: application subtree that depends on auth state.
 *
 * Behavior:
 * - Uses the normal MSAL bootstrap path for all users and environments.
 *
 * Data Source:
 * - hybrid
 *
 * Constraints:
 * - MUST resolve identity through the configured Entra/MSAL contract.
 *
 * Performance:
 * - Keeps auth bootstrap cached and retry-aware so local and deployed flows
 *   converge on the same runtime behavior.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const runtimeBypass = typeof window !== 'undefined'
    ? window.__RUNTIME_CONFIG__?.VITE_AUTH_BYPASS
    : undefined;
  const isDevBypass = getWebConfig().appEnv !== 'prod'
    && (import.meta.env.VITE_AUTH_BYPASS === 'true' || runtimeBypass === 'true');
  if (isDevBypass) {
    return <DevBypassAuthProvider>{children}</DevBypassAuthProvider>;
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
}
